import { Grid, Hidden } from '@mui/material';
import { subDays } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  CardContent,
  CardDetails,
  CardHeader
} from 'src/components/shared/Cards/Card';
import { DashboardCard } from 'src/components/shared/Cards/DashboardCard';
import {
  Divider,
  VerticalDivider
} from 'src/components/shared/Divider/Divider';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { Header3 } from 'src/components/shared/Typography/Header3';
import { Header4 } from 'src/components/shared/Typography/Header4';
import { LeadType } from 'src/components/shared/Typography/LeadType';
import { useReduxDispatch } from 'src/store';
import { GradeDto } from 'src/store/grades/grade';
import { selectActiveGrades } from 'src/store/grades/grades.selectors';
import { fetchNumberOfOngoingMixtures } from 'src/store/mixtures/mixtures.actions';
import { selectNumberOfOngoingMixtures } from 'src/store/mixtures/mixtures.selectors';
import {
  fetchProductsByStatus,
  fetchProductsCreated
} from 'src/store/products/products.actions';
import {
  selectProductsByStatus,
  selectProductsCreated
} from 'src/store/products/products.selectors';
import { RunSystemDto } from 'src/store/runsystems/runsystem';
import { selectActiveRunSystems } from 'src/store/runsystems/runsystems.selectors';
import { ProductsCreatedChart } from './Charts/ProductsCreatedChart';
import { ProductsStatusChart } from './Charts/ProductsStatusChart';

interface DateFilterOption {
  id: number;
  label: string;
  value: number;
}

export const DashboardPage = () => {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const [selectedRunSystems, setSelectedRunSystems] = useState<RunSystemDto[]>(
    []
  );
  const [selectedGrades, setSelectedGrades] = useState<GradeDto[]>([]);

  const [dateFilterOptions] = useState<DateFilterOption[]>([
    {
      id: 1,
      label: t('dateFilter.today'),
      value: 0
    },
    {
      id: 2,
      label: t('dateFilter.last7days'),
      value: 7
    },
    {
      id: 3,
      label: t('dateFilter.last30days'),
      value: 30
    },
    {
      id: 4,
      label: t('dateFilter.last90days'),
      value: 90
    }
  ]);
  const [dateFilterOption, setDateFilterOption] = useState<DateFilterOption>(
    dateFilterOptions[1]
  );
  const [fromDate, setFromDate] = useState<Date>(subDays(new Date(), 7));
  const [toDate] = useState<Date>(new Date());
  const [producedAmount, setProducedAmount] = useState<number>(0);

  const productsByStatus = useSelector(selectProductsByStatus);
  const productsCreated = useSelector(selectProductsCreated);
  const numberOfOngoingMixtures = useSelector(selectNumberOfOngoingMixtures);
  const runSystems = useSelector(selectActiveRunSystems);
  const grades = useSelector(selectActiveGrades);

  useEffect(() => {
    dispatch(fetchNumberOfOngoingMixtures());
  }, [dispatch]);

  useEffect(() => {
    const runSystemIds = selectedRunSystems.map(x => x.id);
    const gradeIds = selectedGrades.map(x => x.id);
    dispatch(fetchProductsByStatus(fromDate, toDate, runSystemIds, gradeIds));
    dispatch(fetchProductsCreated(fromDate, toDate, runSystemIds, gradeIds));
  }, [dispatch, fromDate, toDate, selectedRunSystems, selectedGrades]);

  useEffect(() => {
    const amount = productsCreated.reduce(
      (acc, cur) => acc + cur.producedAmount,
      0
    );
    setProducedAmount(amount);
  }, [productsCreated]);

  return (
    <React.Fragment>
      <div className="flex align-center">
        <Autocomplete
          options={dateFilterOptions}
          label={t('dateFilter.label')}
          value={dateFilterOption}
          optionField="label"
          onChange={option => {
            if (!!option) {
              setDateFilterOption(option);
              setFromDate(subDays(new Date(), option.value));
            }
          }}
          className="mb-0 mr-24"
          style={{ width: 240 }}
        />

        <Autocomplete
          multiple
          options={runSystems}
          label={t('runSystem.singular')}
          optionField="name"
          onChange={runSystems => setSelectedRunSystems(runSystems)}
          className="mb-0 mr-24"
          style={{ width: 340 }}
        />
        <Autocomplete
          multiple
          options={grades}
          label={t('grade.singular')}
          optionField="name"
          onChange={grades => setSelectedGrades(grades)}
          className="mb-0"
          style={{ width: 340 }}
        />
      </div>

      <Divider />

      <Grid container className="mt-12">
        <Grid item md={12} lg={4}>
          <DashboardCard
            icon={<i className="las la-industry" />}
            onClick={() => navigate('/products')}
          >
            <CardHeader>
              <Header4>{t('product.producedAmount')}</Header4>
              <i className="las la-arrow-right" />
            </CardHeader>

            <CardContent>
              <CardDetails>
                <Header3>
                  {producedAmount.toLocaleString('fr')} <LeadType>Kg</LeadType>
                </Header3>
                <span>{dateFilterOption?.label}</span>
              </CardDetails>
            </CardContent>
          </DashboardCard>
        </Grid>
        <Grid item md={12} lg={4}>
          <DashboardCard
            icon={<i className="las la-industry" />}
            onClick={() => navigate('/products')}
          >
            <CardHeader>
              <Header4>{t('product.accepted')}</Header4>
              <i className="las la-arrow-right" />
            </CardHeader>

            <CardContent>
              <CardDetails>
                <Header3>{productsByStatus?.accepted ?? 0}</Header3>
                <span>{t('productStatus.accepted')}</span>
              </CardDetails>
              <CardDetails>
                <Header3>
                  {productsByStatus?.acceptedWithDeviation ?? 0}
                </Header3>
                <span>{t('productStatus.acceptedWithDeviation')}</span>
              </CardDetails>
            </CardContent>
          </DashboardCard>
        </Grid>
        <Grid item md={12} lg={4}>
          <DashboardCard
            icon={<i className="las la-blender" />}
            onClick={() => navigate('/mixtures')}
          >
            <CardHeader>
              <Header4>{t('mixture.numberOfOngoingMixtures')}</Header4>
              <i className="las la-arrow-right" />
            </CardHeader>

            <CardContent>
              <CardDetails>
                <Header3>{numberOfOngoingMixtures}</Header3>
                <span>{t('mixture.ongoing')}</span>
              </CardDetails>
            </CardContent>
          </DashboardCard>
        </Grid>
      </Grid>

      <Divider className="mt-64 mb-48" />

      <Grid container>
        <Grid item xs={12} md={6}>
          <ProductsCreatedChart productsCreated={productsCreated} />
        </Grid>
        <Hidden only={['xs', 'md']}>
          <Grid item lg={1} className="flex jc-center">
            <VerticalDivider />
          </Grid>
        </Hidden>
        <Grid item xs={12} md={6} lg={5}>
          <ProductsStatusChart productsByStatus={productsByStatus} />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
